import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getPopularArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getPopularArticles(4).filter((a) => a.id !== article.id).slice(0, 3);

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      image: article.image,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: '福利社导航',
      },
      publisher: {
        '@type': 'Organization',
        name: '福利社导航',
        logo: {
          '@type': 'ImageObject',
          url: 'https://fulishe-dh.com/logo.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://fulishe-dh.com/article/${article.id}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulishe-dh.com/' },
        { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://fulishe-dh.com/category/${article.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://fulishe-dh.com/article/${article.id}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://fulishe-dh.com/article/${article.id}`}
        ogType="article"
        schemaData={schemaData}
      />

      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-14">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="bg-accent-100 text-accent-700 text-xs px-3 py-1 rounded-full font-medium cursor-pointer hover:bg-accent-200 transition-colors"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次浏览
              </span>
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {article.description}
            </p>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 福利社导航`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-heading
              [&_h3]:text-base [&_h3]:md:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:md:text-base
              [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:space-y-2
              [&_li]:text-sm [&_li]:md:text-base [&_li]:leading-relaxed [&_li]:text-foreground-700
              [&_strong]:text-foreground-900 [&_strong]:font-semibold
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-xs text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-secondary-100 text-secondary-800 text-xs px-3 py-1 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-xs font-semibold text-foreground-950 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}